import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Link, Stack, Typography,
} from '@mui/material';
import { MenuBook as NoteIcon } from '@mui/icons-material';
import { subscribe } from '../../services/messageBox';
import knowledgeService, { notePath, openNotesPage } from '../../services/knowledgeService';

const SEVERITY_ORDER = { error: 0, warning: 1, info: 2, success: 3 };
const HEADINGS = { error: 'Error', warning: 'Warning', info: 'Information', success: 'Done' };

/**
 * The Biztimize Note(s) that explain an error code. A released Note links by
 * its number; with none (yet), the code still links to a Notes search, so the
 * user always has somewhere to go.
 */
const NoteLinks = ({ code }) => {
  const [notes, setNotes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    knowledgeService.notesForErrorCode(code)
      .then(({ data }) => { if (!cancelled) setNotes(data.results || []); })
      .catch(() => { if (!cancelled) setNotes([]); });
    return () => { cancelled = true; };
  }, [code]);

  if (notes === null) return null;
  if (notes.length === 0) {
    return (
      <Link component="button" variant="body2"
            onClick={() => openNotesPage(`/notes/?q=${encodeURIComponent(code)}`)}>
        Search Biztimize Notes for {code}
      </Link>
    );
  }
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      {notes.map((n) => (
        <Link key={n.number} component="button" variant="body2" sx={{ textAlign: 'left' }}
              onClick={() => openNotesPage(notePath(n.number))}>
          Biztimize Note {n.number}: {n.title}
        </Link>
      ))}
    </Stack>
  );
};

/**
 * The application's Message Box (services/messageBox.js). Mounted once, at the
 * root. Messages arriving while it is open are added to the same box, errors
 * first; each one with an error code shows the code and its Biztimize Note.
 */
const MessageBoxHost = () => {
  const [messages, setMessages] = useState([]);

  useEffect(() => subscribe((incoming) => {
    setMessages((current) => {
      const seen = new Set(current.map((m) => `${m.severity}|${m.text}`));
      const merged = [...current, ...incoming.filter((m) => !seen.has(`${m.severity}|${m.text}`))];
      return merged.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
    });
  }), []);

  const open = messages.length > 0;
  const worst = open ? messages[0].severity : 'info';
  const close = () => setMessages([]);

  return (
    <Dialog open={open} onClose={close} maxWidth="sm" fullWidth
            aria-labelledby="message-box-title">
      <DialogTitle id="message-box-title">
        {messages.length > 1 ? `${messages.length} messages` : (messages[0]?.title || HEADINGS[worst])}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {messages.map((m, i) => (
            <Alert key={`${m.severity}-${i}`} severity={m.severity} variant="outlined">
              {messages.length > 1 && m.title && <AlertTitle>{m.title}</AlertTitle>}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{m.text}</Typography>
              {m.error_code && (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" icon={<NoteIcon />} label={m.error_code} variant="outlined"
                        sx={{ fontFamily: 'monospace' }} />
                  <NoteLinks code={m.error_code} />
                </Box>
              )}
            </Alert>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} variant="contained" autoFocus>OK</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageBoxHost;
